import type { Vec3 } from '../../geo/types';
import type { NightLight, NightLightSource } from '../../world/types';

export const NIGHT = {
  /** Lights whose light the final pass lays on the surfaces around them: those nearest the eye. */
  pools: 24,
  /** Glows the glow pass can draw at once: a candle in most houses, the lanterns and the ships. */
  glows: 4096,
  /* Least radius in pixels a glow is drawn at, so a far flame stays a point of light instead of
     falling between the pixels; it is dimmed by the share of that radius it would really fill. */
  minRadiusPx: 2.2,
  minGain: 0.8,
} as const;

/**
 * A wax or tallow flame behind horn, and one candle seen through the leaded glass of a room it
 * hardly lights: far dimmer and deeper in colour. At the flame's own strength a window read as a
 * room on fire (the user, 7 October 2026). Both are set by eye.
 */
export const FLAME_COLOUR = [1, 0.62, 0.26] as const;
export const PANE_COLOUR = [0.58, 0.33, 0.12] as const;

export function burns(light: NightLight, burning: number): boolean {
  return light.from === undefined || light.from <= burning;
}

/*
 * A candle's flame: nearly steady, with a slow swell and a quick shiver. glow.vert draws the glows
 * by the same three sines.
 */
export function flicker(phase: number, timeSeconds: number): number {
  const wave = (rate: number, turn: number): number =>
    0.5 + 0.5 * Math.sin(timeSeconds * rate + phase * turn);
  return 1 - 0.1 * wave(9.1, 1) * wave(3.7, 1.7) - 0.05 * wave(1.3, 2.3);
}

/** The burning lights nearest `eye`, nearest first, each counted from the edge of its reach. */
export function nearestLights(
  lights: readonly NightLight[],
  eye: Vec3,
  burning: number,
  count: number
): NightLight[] {
  const ranked: { light: NightLight; gap: number }[] = [];
  for (const light of lights) {
    // A candle behind glass carries nowhere: it shows as its glow alone.
    if (light.reach <= 0 || !burns(light, burning)) continue;
    const [x, y, z] = light.position;
    ranked.push({ light, gap: Math.hypot(x - eye[0], y - eye[1], z - eye[2]) - light.reach });
  }
  ranked.sort((a, b) => a.gap - b.gap);
  return ranked.slice(0, count).map((entry) => entry.light);
}

/**
 * Every source's lights as one list, built anew only for the sources whose lights move; the lights
 * that stand still are gathered once, the first time darkness asks for them. They come first and
 * keep their places, so the glow layer writes each of them once and not every frame.
 */
export function gatherLights(sources: readonly NightLightSource[]): {
  list(): readonly NightLight[];
} {
  let fixed: readonly NightLight[] | null = null;
  const moving = sources.filter((source) => source.moving);
  return {
    list() {
      fixed ??= sources.filter((source) => !source.moving).flatMap((source) => source.list());
      return moving.length ? [...fixed, ...moving.flatMap((source) => source.list())] : fixed;
    },
  };
}
